import React, {createContext, useState, useContext, useCallback} from 'react';
import {UUID} from 'bson';
import {Message} from '../Types/MessageTypes';

interface MessageContextType {
  messages: Message[];
  addMessage: (message: Omit<Message, 'key'> & {key?: string}) => string;
  removeMessage: (key: string) => void;
  clearMessages: () => void;
}

const MessageContext = createContext<MessageContextType | undefined>(undefined);

export const MessageProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);

  const removeMessage = useCallback((key: string) => {
    setMessages(prev => prev.filter(msg => msg.key !== key));
  }, []);

  const addMessage: MessageContextType['addMessage'] = useCallback(
    msg => {
      const key = msg.key || new UUID().toString();

      setMessages(prev => {
        // Prevent duplicate keys
        if (prev.some(m => m.key === key)) {
          return prev;
        }

        const newMessage: Message = {...msg, key};

        // Set up auto-dismiss timer
        if (msg.durationMs) {
          setTimeout(() => removeMessage(key), msg.durationMs);
        }

        return [...prev, newMessage];
      });

      return key;
    },
    [removeMessage],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return (
    <MessageContext.Provider
      value={{messages, addMessage, removeMessage, clearMessages}}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessages = (): MessageContextType => {
  const ctx = useContext(MessageContext);
  if (!ctx)
    throw new Error('useMessages must be used within a MessageProvider');
  return ctx;
};
